// 「最近の関心」総括画面
// 直近 N 日のメモ付き書籍を AI に渡し、繰り返し現れる主題 3 つを浮かび上がらせる。
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderHistorySection,
  bindHistoryHandlers,
} from '../ui.js';
import { fetchRecentFocus, fetchAllNotesParallel } from '../api.js';
import { getAllBooks, upsertBooks, saveAiHistory, getAiHistoryByType } from '../cache.js';

const DEFAULT_DAYS = 60;

export async function showRecentFocusScreen() {
  const histItems = await getAiHistoryByType('recent-focus');
  renderEntry(histItems);
}

function renderEntry(histItems) {
  const html = `
    ${screenHead('最近の関心')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      直近のメモから「今あなたが繰り返し考えている主題」を 3 つに整理します。<br/>
      期間を選んで開始してください。
    </p>
    <div class="form-group">
      <label class="form-label">対象期間</label>
      <select class="form-select" id="rf-days">
        <option value="30">直近 30 日</option>
        <option value="60" selected>直近 60 日</option>
        <option value="90">直近 90 日</option>
        <option value="180">直近 半年</option>
      </select>
    </div>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>関心の主題を抽出する</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 · 1 回あたり 約 5-12 円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('recent-focus', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-go').addEventListener('click', async () => {
    const days = parseInt(screen.querySelector('#rf-days').value, 10) || DEFAULT_DAYS;
    await runFocus(days);
  });
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderResult(entry.payload, entry.context?.days || DEFAULT_DAYS),
    () => showRecentFocusScreen()
  );
}

async function runFocus(days) {
  const all = await getAllBooks();
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  // 対象: 直近 N 日に endDate/updatedAt があるもの、または現在読書中
  const target = all.filter((b) => {
    if (b.status === '読書中') return true;
    const end = b.endDate ? Date.parse(b.endDate) : 0;
    if (end && end >= cutoff) return true;
    const upd = b.updatedAt ? Date.parse(b.updatedAt) : 0;
    return upd >= cutoff;
  });
  if (target.length < 2) {
    toast(`直近 ${days} 日に対象書籍が少なすぎます`);
    return;
  }
  // メモが空のものを補完
  const missing = target.filter((b) => !b.note).length;
  if (missing > 0) {
    showLoading(`書籍メモを補完中… (0/${missing})`);
    await fetchAllNotesParallel(target, (done, total) => {
      const ov = document.getElementById('overlay-msg');
      if (ov) ov.textContent = `書籍メモを補完中… (${done}/${total})`;
    });
    await upsertBooks(target);
    hideLoading();
  }
  const withNotes = target.filter((b) => (b.note || '').trim().length > 60);
  if (withNotes.length < 2) {
    toast('メモのある書籍が少なすぎます');
    return;
  }

  showLoading('AI が関心の主題を抽出中…');
  try {
    const payload = withNotes.map((b) => ({
      name: b.name,
      author: b.author,
      note: b.note,
      endDate: b.endDate,
      status: b.status,
    }));
    const result = await fetchRecentFocus(payload, days);
    await saveAiHistory({
      type: 'recent-focus',
      title: `最近の関心 (${days}日 / ${withNotes.length}冊)`,
      subtitle: (result.focuses || []).slice(0, 3).map((f) => f.theme).join(' / '),
      context: { days, count: withNotes.length },
      payload: result,
    });
    renderResult(result, days);
  } catch (e) {
    console.error(e);
    toast('抽出に失敗しました');
  } finally {
    hideLoading();
  }
}

export function renderFromHistory(entry) {
  renderResult(entry.payload, entry.context?.days || DEFAULT_DAYS);
}

function renderResult(data, days) {
  const focuses = data.focuses || [];
  const html = `
    ${screenHead('最近の関心')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 24px;">
      直近 ${days} 日のメモから ${focuses.length} つの主題を抽出しました。
    </p>
    ${focuses
      .map(
        (f, i) => `
      <article class="focus-card">
        <p class="focus-card__order">主題 ${i + 1}</p>
        <h3 class="focus-card__title">${escapeHtml(f.theme || '')}</h3>
        <p class="focus-card__summary">${escapeHtml(f.summary || '')}</p>
        ${
          f.books?.length
            ? `<div class="focus-card__books"><span class="focus-card__label">関連書籍</span>${f.books
                .map((b) => `<span class="chip">${escapeHtml(b)}</span>`)
                .join('')}</div>`
            : ''
        }
        ${
          f.question
            ? `<div class="focus-card__q"><span class="focus-card__label">次に問うべき</span><p>${escapeHtml(f.question)}</p></div>`
            : ''
        }
      </article>
    `
      )
      .join('')}
    ${
      data.commentary
        ? `<section class="focus-commentary">
             <p class="section-label">commentary</p>
             <p style="margin:0;font-family:var(--font-serif);font-size:14px;line-height:1.85;color:var(--dim);">${escapeHtml(data.commentary)}</p>
           </section>`
        : ''
    }
    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>期間を変えてやり直す</span>
    </button>
  `;
  const screen = showScreen('recent-focus', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', () => showRecentFocusScreen());
}
